import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TaskManagementApi } from "../routes/taskManagement";
import { apiLogger, authLogger } from "../Utils/logger";
import {
  invalidateLevelQueries,
  invalidateTaskQueries,
} from "../Utils/queryInvalidation";

export const useGetMitTasks = (enabled = true) => {
  return useQuery({
    queryKey: ["taskManagement", "mitTasks"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/tasks/mit-lists");
        const response = await TaskManagementApi.getMitTasks();
        authLogger.apiResponse("GET", "/tasks/mit-lists", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/tasks/mit-lists",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
  });
};

export const useGetDoTasks = (enabled = true) => {
  return useQuery({
    queryKey: ["taskManagement", "doLists"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/tasks/do-lists");
        const response = await TaskManagementApi.getDoTasks();
        authLogger.apiResponse("GET", "/tasks/do-lists", response, 200);

        // Extract the data from response.data.data (following the old pattern)
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/tasks/do-lists",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useChangeTaskLevel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      try {
        const payload = {
          level: data?.level,
          completion_status: data?.completion_status,
        };
        if (data?.scheduled_for) {
          payload.scheduled_for = data.scheduled_for;
        }
        const taskId = data?.taskId;

        apiLogger.apiRequest("PATCH", `/tasks/${taskId}/change-level`, {
          data: data,
        });
        const res = await TaskManagementApi.changeTaskLevel(taskId, payload);
        apiLogger.apiResponse(
          "PATCH",
          `/tasks/${taskId}/change-level`,
          res.data,
          200,
        );
        return res.data.data;
      } catch (error) {
        apiLogger.apiError(
          "PATCH",
          `/tasks/${data?.taskId}/change-level`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries based on the task level change
      const { sourceColumnId, destinationColumnId } = variables;

      // Use centralized query invalidation for level-up detection
      invalidateLevelQueries(queryClient);
      invalidateTaskQueries(queryClient);

      // Always invalidate the main tasks by date query
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "tasksByDate"],
      });
      queryClient.invalidateQueries({ queryKey: ["goalsAndTargets", "goals"] });

      // Invalidate specific queries based on the move direction
      if (sourceColumnId === "hitList" && destinationColumnId === "mitList") {
        // HIT -> MIT: invalidate MIT tasks
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "mitTasks"],
        });
      } else if (
        sourceColumnId === "hitList" &&
        destinationColumnId === "doList"
      ) {
        // HIT -> DO: invalidate DO tasks
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "doLists"],
        });
      } else if (
        sourceColumnId === "mitList" &&
        destinationColumnId === "hitList"
      ) {
        // MIT -> HIT: invalidate MIT tasks (since task moved out)
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "mitTasks"],
        });
      } else if (
        sourceColumnId === "doList" &&
        destinationColumnId === "hitList"
      ) {
        // DO -> HIT: invalidate DO tasks (since task moved out)
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "doLists"],
        });
      } else if (sourceColumnId === "completedList") {
        // Moving from completed back to active lists
        if (destinationColumnId === "mitList") {
          queryClient.invalidateQueries({
            queryKey: ["taskManagement", "mitTasks"],
          });
        } else if (destinationColumnId === "doList") {
          queryClient.invalidateQueries({
            queryKey: ["taskManagement", "doLists"],
          });
        }
      } else if (destinationColumnId === "completedList") {
        // Moving to completed - invalidate both MIT and DO as task could come from either
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "mitTasks"],
        });
        queryClient.invalidateQueries({
          queryKey: ["taskManagement", "doLists"],
        });
      }
    },
  });
};

export const useMarkSubtaskAsCompleted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      try {
        apiLogger.apiRequest(
          "PATCH",
          `/tasks/${data.taskId}/subtasks/${data.subtaskId}/mark-as-completed`,
        );
        const response = await TaskManagementApi.markSubtaskAsCompleted(
          data.taskId,
          data.subtaskId,
        );
        apiLogger.apiResponse(
          "PATCH",
          `/tasks/${data.taskId}/subtasks/${data.subtaskId}/mark-as-completed`,
          response.data,
          200,
        );
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "PATCH",
          `/tasks/${data.taskId}/subtasks/${data.subtaskId}/mark-as-completed`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      // Use centralized query invalidation for level-up detection
      invalidateLevelQueries(queryClient);
      invalidateTaskQueries(queryClient);
    },
  });
};

export const useDeleteSubtask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subtaskId) => {
      try {
        apiLogger.apiRequest("DELETE", `/subtasks/${subtaskId}`);
        const response = await TaskManagementApi.deleteSubtask(subtaskId);
        apiLogger.apiResponse(
          "DELETE",
          `/subtasks/${subtaskId}`,
          response.data,
          200,
        );
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "DELETE",
          `/subtasks/${subtaskId}`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "mitTasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "doLists"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "tasksByDate"],
      });
    },
  });
};

// New: Fetch kanban tasks by date (replacing actions/api usage in TaskMainBox)
export const useGetTasksByDate = (date, enabled = true) => {
  return useQuery({
    queryKey: ["taskManagement", "tasksByDate", date],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/tasks", { date });
        const response = await TaskManagementApi.getTasksByDate(date);
        authLogger.apiResponse("GET", "/tasks", response, 200);
        return response?.data || response;
      } catch (error) {
        authLogger.apiError("GET", "/tasks", error, error.response?.status);
        throw error;
      }
    },
    enabled: enabled && !!date,
    retry: 1,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};

export const useGetTaskEnums = (enabled = true) => {
  return useQuery({
    queryKey: ["taskManagement", "enums"],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", "/tasks/enums");
        const response = await TaskManagementApi.getTaskEnums();
        authLogger.apiResponse("GET", "/tasks/enums", response, 200);
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          "/tasks/enums",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled,
    retry: 1,
  });
};

export const useGetTaskById = (taskId, enabled = true) => {
  return useQuery({
    queryKey: ["taskManagement", "task", taskId],
    queryFn: async () => {
      try {
        authLogger.apiRequest("GET", `/tasks/${taskId}`);
        const response = await TaskManagementApi.getTaskById(taskId);
        authLogger.apiResponse("GET", `/tasks/${taskId}`, response, 200);
        return response?.data || response;
      } catch (error) {
        authLogger.apiError(
          "GET",
          `/tasks/${taskId}`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled: enabled && !!taskId,
    retry: 1,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      try {
        apiLogger.apiRequest("POST", "/tasks", payload);
        const response = await TaskManagementApi.createTask(payload);
        apiLogger.apiResponse("POST", "/tasks", response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError("POST", "/tasks", error, error.response?.status);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "mitTasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "doLists"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "tasksByDate"],
      });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, payload }) => {
      try {
        apiLogger.apiRequest("PUT", `/tasks/${taskId}`, payload);
        const response = await TaskManagementApi.updateTask(taskId, payload);
        apiLogger.apiResponse("PUT", `/tasks/${taskId}`, response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "PUT",
          `/tasks/${taskId}`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "mitTasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "doLists"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "tasksByDate"],
      });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, payload = {} }) => {
      try {
        apiLogger.apiRequest("DELETE", `/tasks/${taskId}`, payload);
        const response = await TaskManagementApi.deleteTask(taskId, payload);
        apiLogger.apiResponse("DELETE", `/tasks/${taskId}`, response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "DELETE",
          `/tasks/${taskId}`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "mitTasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "doLists"],
      });
      queryClient.invalidateQueries({
        queryKey: ["taskManagement", "tasksByDate"],
      });
    },
  });
};
export const useGetDoneAchievedTasks = (params = {}) => {
  return useQuery({
    queryKey: ["taskManagement", "doneAchievedTasks"],
    queryFn: async () => {
      try {
        apiLogger.apiRequest("GET", "/tasks/today-completed", params);
        const response = await TaskManagementApi.getDoneAchievedTasks(params);
        apiLogger.apiResponse("GET", "/tasks/today-completed", response, 200);

        return response?.data || response;
      } catch (error) {
        apiLogger.apiError(
          "GET",
          "/tasks/today-completed",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    enabled: true,
  });
};

export const useReorderSubtasks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      try {
        const requestBody = { order: data.order };
        apiLogger.apiRequest(
          "POST",
          `/tasks/${data.taskId}/subtasks/reorder`,
          requestBody,
        );
        const response = await TaskManagementApi.reorderSubtasks(
          data.taskId,
          requestBody,
        );
        apiLogger.apiResponse(
          "POST",
          `/tasks/${data.taskId}/reorder-subtasks`,
          response.data,
          200,
        );
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          `/tasks/${data.taskId}/reorder-subtasks`,
          error,
          error.response?.status,
        );
        throw error;
      }
    },
  });
};

export const useGoogleConnectUrl = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        apiLogger.apiRequest("GET", "/api/google/connect-url");

        const response = await TaskManagementApi.getGoogleConnectUrl();

        apiLogger.apiResponse(
          "GET",
          "/api/user/google/connect-url",
          response.data,
          response.status,
        );

        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "GET",
          "/api/user/google/connect-url",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google", "status"] });
    },
  });
};

export const useGoogleDisconnect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        apiLogger.apiRequest("POST", "/api/user/google/disconnect");

        const response = await TaskManagementApi.disconnectGoogle();

        apiLogger.apiResponse(
          "POST",
          "/api/user/google/disconnect",
          response.data,
          response.status,
        );

        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          "/api/user/google/disconnect",
          error,
          error.response?.status,
        );
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: ["google", "status"] });
    },
  });
};
